/**
 * @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
 * @license AGPL-3.0
 *
 * This code is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 *
 * Any use of the work other than as authorized under this license or copyright law is prohibited.
 */

import $ from 'jquery'
import _ from 'underscore'
import { underscoreExtends, us } from '@/browser'
import { deserialize, param as paramUrl, locationHash, redirect, vars } from '@/url'

// shortcut
const slice = Array.prototype.slice

// units
const sizes = ['Byte', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

/**
 * format/printf
 */
export function printf (str, params) {
  str = _.isString(str) ? str : ''
  // is array?
  if (!_.isArray(params)) {
    params = slice.call(arguments, 1)
  }
  let index = 0
  return str
    .replace(
      /%(([0-9]+)\$)?[A-Za-z]/g,
      function (match, pos, n) {
        if (pos) {
          index = n - 1
        }
        // return params[index++];
        const val = params[index++]
        return val !== undefined ? val : 'unknown'
      }
    )
    .replace(/%%/g, '%')
}

/**
 * Serialize object (key/value pairs) to fit into URLs (e.g. a=1&b=2&c=HelloWorld)
 * @param   {object} obj         Object to serialize
 * @param   {string} [delimiter] Delimiter
 * @returns {string}             Serialized object
 * @example
 * _.serialize({ a: 1, b: 2, c: 'text' });
 */
export function serialize (obj, delimiter, replacer) {
  const tmp = []; const e = replacer || encodeURIComponent; let id
  if (typeof obj === 'object') {
    for (id in (obj || {})) {
      tmp.push(e(id) + (obj[id] !== undefined ? `=${e(obj[id])}` : ''))
    }
  }
  return tmp.sort().join(delimiter === undefined ? '&' : delimiter)
}

/**
 * Convenience function; similar to jQuery's $.param but sorted;
 * different delimiter; not escaped; useful for composite keys
 */
export function param (obj) {
  return this.serialize(obj, ' / ', _.identity)
}

// quite like param but also drop undefined values
export function cacheKey (obj) {
  return this.param(Object.assign({}, obj))
}

// extend underscore utilities
_.mixin({
  serialize,
  deserialize,
  param,
  cacheKey
})

_.url = { deserialize, param: paramUrl, hash: locationHash, redirect, vars }

export function getCookie (key) {
  key = String(key || '\u0000')
  return _.chain(document.cookie.split(/; ?/))
    .filter(function (pair) {
      return pair.substr(0, key.length) === key
    })
    .map(function (pair) {
      return decodeURIComponent(pair.substr(key.length + 1))
    })
    .first()
    .value()
}

export function setCookie (key, value, lifetime) {
  // yep, works this way:
  const c = `${key}=${encodeURIComponent(value)}${(lifetime ? `; expires=${new Date(new Date().getTime() + lifetime).toGMTString()}` : '')}; path=/`
  document.cookie = c
}

_.getCookie = getCookie

_.setCookie = setCookie

export function shorten (string = '', limit = 20) {
  return string.length > limit
    ? string.substring(0, limit) + '…'
    : string
}

// simple composite-key constructor/parser
export const cid = (function () {
  function encode (s) {
    // escape dots and backslashes
    return String(s).replace(/([.\\])/g, '\\$1')
  }

  function decode (s) {
    const regex = /(\\\\|\\\.|\.)/g; let match; const parts = ['', '']; let index = 0; let p = 0
    // ensure we have a string
    s = String(s)
    // process string step by step in a loop
    while ((match = regex.exec(s)) !== null) {
      // append substring before the match (use substring; not substr!)
      parts[p] += s.substring(index, match.index)
      // now handle match
      switch (match[0]) {
        // escaped backslash
        case '\\\\': parts[p] += '\\'; break
          // escaped dot
        case '\\.': parts[p] += '.'; break
          // dot
        case '.': p = 1; break
        // no default
      }
      index = match.index + match[0].length
    }
    // add tail (with just 1 parameter substring and substr are identical)
    parts[p] += s.substring(index)
    // some apis use folder (chronos) and some use folder_id
    return p === 0 ? { id: parts[0] } : { folder_id: parts[0], folder: parts[0], id: parts[1] }
  }

  return function (o) {
    let tmp; const r = 'recurrenceId'; let m; let f
    if (typeof o === 'string') {
      // Triples? (incl. new CalDAV ids)
      // id might contain a dash; recurrenceId has a lot of varieties: timestamp, zulu with and without Z at the end, timezone:Zulu
      //                 |folder               |.|id   |.|recurrenceId
      if ((m = o.match(/^(cal:\/\/\d+\/\d+|\d+)\.([0-9-]+)(\.((\w+\/\w+:)?[\dTZ]+))?$/)) && m.length) {
        tmp = { folder_id: String(m[1]), folder: String(m[1]), id: String(m[2]) }
        if (m[4] !== undefined) tmp[r] = String(m[4])
        return tmp
      }
      // character based? (double tuple)
      return decode(o)
    } else if (typeof o === 'object' && o !== null) {
      // join
      tmp = encode(o.id)
      f = o.folder_id !== undefined ? o.folder_id : o.folder
      if (o[r] !== undefined && o[r] !== null) {
        // if we have a recurrence position we need a folder
        tmp = `${encode(f || 0)}.${tmp}.${encode(o[r])}`
      } else if (f !== undefined) {
        tmp = `${encode(f)}.${tmp}`
      }
      return tmp
    }
  }
}())

// escape cid - useful for event handlers
export function ecid (o) {
  const cid = typeof o === 'string' ? o : _.cid(o)
  return encodeURIComponent(cid).replace(/\./g, ':')
}

/**
 * Copy object
 * @param   {object}  elem Object to copy
 * @param   {boolean} deep Deep vs. Shallow copy
 * @returns {object}       Its clone
 */
export const copy = (function () {
  const isArray = _.isArray
  function copy (elem, deep) {
    const tmp = isArray(elem) ? new Array(elem.length) : {}; let prop; let i
    for (i in elem) {
      prop = elem[i]
      tmp[i] = deep && typeof prop === 'object' && prop !== null ? copy(prop, deep) : prop
    }
    return tmp
  }

  return function (elem, deep) {
    return typeof elem !== 'object' || elem === null ? elem : copy(elem, !!deep)
  }
}())

/**
 * This function simply writes its parameters to console.
 * Useful to debug callbacks, e.g. event handlers.
 * @returns {object} First parameter to support inline inspecting
 * @example
 * http.GET({ module: 'calendar', params: { id: 158302 }, success: _.inspect });
 */
export function inspect (first) {
  const args = slice.call(arguments)
  args.unshift('Inspect')
  console.debug.apply(console, args)
  return first
}

/**
 * Call function if first parameter is a function (simplifies callbacks)
 * @param {Function} fn Callback
 */
export function call (fn) {
  if (_.isFunction(fn)) {
    let i = 1; const $l = arguments.length; const args = []
    for (; i < $l; i++) {
      args.push(arguments[i])
    }
    return fn.apply(fn, args)
  }
}

/**
 * Returns local current time as timestamp
 * @returns {long} Timestamp
 */
export function now () {
  return Date.now()
}

// legacy
export function deepClone (elem) {
  return _.copy(elem, true)
}

/**
 * Last function only
 * Works with non-anonymous functions only
 */
export function lfo () {
  // call counter
  const curry = slice.call(arguments)
  let sync = false
  let context
  // sync or async (default)
  if (curry[0] === true) { curry.shift(); sync = true }
  // context?
  if (!_.isFunction(curry[0])) context = curry.shift()
  // get function and count
  const fn = curry.shift() || $.noop
  const count = (fn.count = (fn.count || 0) + 1)
  // wrap
  return function () {
    const args = slice.call(arguments)
    function cont () {
      if (count === fn.count) {
        fn.apply(context || fn, curry.concat(args))
      }
    }
    if (sync) cont(); else setTimeout(cont, 0)
  }
}

/**
 * Array-based printf uses a callback function to collect parts of the
 * format string in an array. Useful to construct DOM nodes based on
 * translated text.
 * @param {string}          str      the format string using printf syntax.
 * @param {Function(index)} formatCb A callback function which converts
 *                                   a format specifier to an array element. Its parameter is a 0-based
 *                                   index of the format specifier.
 * @param {Function(text)}  textCb   An optional callback function which
 *                                   converts text between format specifiers to an array element. Its
 *                                   parameter is the text to convert. If not specified, formatCb is
 *                                   called for both, format specifiers and the text between them.
 */
export function aprintf (str, formatCb, textCb) {
  const result = []; let index = 0
  if (!textCb) textCb = formatCb || $.noop
  String(str).replace(
    /%(([0-9]+)\$)?[A-Za-z]|((?:%%|[^%])+)/g,
    function (match, pos, n, text) {
      if (text) {
        result.push(textCb(text.replace(/%%/g, '%')))
      } else {
        if (pos) index = n - 1
        result.push(formatCb(index++))
      }
      return ''
    })
  return result
}

/**
 * return human readable filesize
 * @param  {numeric} size             in bytes
 * @param  {object}  options
 * @param  {numeric} options.digits   (number of digits) appear after the decimal point
 * @param  {boolean} options.force    using fixed-point notation
 * @param  {string}  options.zerochar replace value of 0 with this char
 * @return {string}
 */
export function filesize (size, options) {
  const opt = _.extend({
    digits: 0,
    force: true,
    zerochar: undefined
  }, options || {})

  // for security so math.pow doesn't get really high values
  if (opt.digits > 10) {
    opt.digits = 10
  }
  let i = 0; const dp = Math.pow(10, opt.digits || 0)
  while (size > 1024 && i < sizes.length) {
    size = size / 1024
    i++
  }
  const val = (Math.round(size * dp, 1) / dp)
  // replacement to 0
  if (typeof opt.zerochar !== 'undefined' && val === 0) return opt.zerochar

  return `${(opt.force ? val : val.toFixed(opt.digits))}\xA0${sizes[i]}`
}

/**
 * shortens a string
 * @param  {string}  str
 * @param  {object}  options
 * @param  {number}  options.max:               max length
 * @param  {string}  options.char:              ellipsis char
 * @param  {string}  options.charpos:           'middle' or 'end'
 * @param  {number}  options.length:            if charpos 'middle' value defines length of head and tail part
 * @param  {boolean} options.suppressExtension: do not display the file extension in case this flag does exist and also equals the true value.
 * @param  {boolean} options.optimizeWordbreak: manipulate <str> in a way that text will flow and break with an optimum of used render space in case this flag does exist and also equals the true value.
 * @return {string}
 */
export function ellipsis (str, options) {
  let space
  let undefinedValue

  // be robust
  str = String(str || '').trim()

  options = _.extend({

    max: 70,
    char: '\u2026',
    charpos: 'end',
    length: undefinedValue

  }, (options || {}))

  // do not display a file's extension
  if (options.suppressExtension === true) {
    str = (str.split(/.[^.]*$/)[0]).trim() || str
  }

  // compute ellipsis
  if (str.length > options.max) {
    space = options.max - options.char.length

    if (options.charpos === 'end') {
      str = str.substr(0, (options.max - options.char.length)) + options.char
    } else {
      // fix invalid length
      if (!options.length || ((options.length * 2) > space)) {
        // save space for ellipse char
        options.length = (((space % 2) === 0) ? (space / 2) : ((options.max / 2) - 1)) || 1
      }
      str = str.substr(0, options.length).trim() + options.char + str.substr(str.length - options.length).trim()
    }
  }
  if (options.optimizeWordbreak === true) {
    str = makeStringBreakable(str)
  }
  return str // exactly one exit point.
}

function makeStringBreakable (str) {
  // https://www.cs.tut.fi/~jkorpela/dashes.html
  const regXSearchHyphensGlobally = (/[\u002D\u1806\u2010\u2012\u2013\u2014\u2015\u207B\u208B\u2212]+/g)

  // http://es5.github.com/#WhiteSpace
  // https://www.cs.tut.fi/~jkorpela/chars/spaces.html

  /* eslint-disable-next-line no-control-regex */
  const regXSearchWhitespaceGlobally = (/[\x09\x0A\x0B\x0C\x0D\x20\xA0\u1680\u180E\u2000\u2001\u2002\u2003\u2004\u2005\u2006\u2007\u2008\u2009\u200A\u202F\u205F\u3000\u2028\u2029]+/g)
  const regXSearchZeroWhitespaceGlobally = (/[\u200B\uFEFF]+/g)

  // softHyphenChar = '\u00AD',
  const nonBreakingHyphenChar = '\u2011'
  const nonBreakingWhitespaceChar = '\u00A0'
  const breakingZeroWhitespaceChar = '\u200B'

  return (str
    .replace(regXSearchZeroWhitespaceGlobally, '')
    .replace(regXSearchWhitespaceGlobally, nonBreakingWhitespaceChar)
    .replace(regXSearchHyphensGlobally, nonBreakingHyphenChar)
    .split('')
    .join(breakingZeroWhitespaceChar)
  )
}

// makes sure you have an array
export function getArray (o) {
  return _.isArray(o) ? o : [o]
}

export function wait (t) {
  let def = $.Deferred()
  setTimeout(function () {
    def.resolve()
    def = null
  }, t || 0)
  return def
}

export function toHash (array, prop) {
  const tmp = {}
  _(array).each(function (obj) {
    if (obj && prop && _.isString(prop)) tmp[obj[prop]] = obj
  })
  return tmp
}

export const noI18n = _.url.hash('debug-i18n')
  ? function (text) {
    return `\u200b${(_.isString(text) ? text.replace(/[\u200b\u200c]/g, '') : text)}\u200c`
  }
  : function (text) { return text } // do not pollute `_.identity` with new properties (see below)

_.noI18n = noI18n
/**
 * In "debug-i18n" mode, strips the translation markers from the passed
 * string. Useful if a translated string needs to be used for any data
 * processing instead of being displayed in the GUI (e.g. the translated
 * default name "Unnamed" for a new file to be created via a server call).
 *
 * @param   {string} text The text to be stripped (e.g.: "\u200bUnnamed\u200c").
 *
 * @returns {string}      The stripped text (e.g.: "Unnamed").
 */
_.noI18n.fix = _.url.hash('debug-i18n')
  ? function (text) {
    return text.replace(/^\u200b|\u200c$/g, '')
  }
  : _.identity

/**
 * Similar to `_.printf`: Takes a format string and arguments to be filled
 * for the placeholders. Note that this method does not do any translation
 * by itself.
 *
 * @param   {string}    pattern The format string to be processed, usually the result of one of the
 *                              gettext functions, or some other language-independent pattern.
 *
 * @param   {unknown[]} args    The arguments to be inserted for the placeholders in the format string.
 *                              In "debug-i18n" mode, the translation markers will be removed from all
 *                              arguments.
 *
 * @returns {string}            The processed string (enclosed in translation markers in "debug-i18n"
 *                              mode).
 */
_.noI18n.format = printf
if (_.url.hash('debug-i18n')) {
  _.noI18n.format = function (/* pattern, ...args */) {
    return _.noI18n(printf.apply(_, arguments))
  }
}

/**
 * Similar to `_.aprintf`: Takes a format string, and returns an array with
 * the results of one or two callback functions processing the plain text
 * and placeholders. Useful e.g. to replace the placeholders in the format
 * string with DOM nodes or HTML mark-up that can be passed to `$.append`
 * and similar functions.
 *
 * @param   {string}                pattern           The format string to be processed, usually the result of one of the
 *                                                    gettext functions.
 * @param   {(index: number) => T1} argFn             The callback function used to process the placeholders in the pattern
 *                                                    string. Receives the zero-based (!) index of the placeholder (also for
 *                                                    indexed placeholders, e.g. the index 2 for "%3$s").
 * @param   {(text: string) => T2}  [textFn=_.noI18n] The callback function used to process the text portions between the
 *                                                    placeholders. If omitted, uses the `_.noI18n` function that returns the
 *                                                    passed text portion (enclosed in translation markers in "debug-i18n"
 *                                                    mode).
 * @returns {Array<T1|T2>}                            The results of the callback functions, in order of the text portions in
 *                                                    the passed format string.
 */
_.noI18n.assemble = function (pattern, argFn, textFn) {
  return aprintf(_.noI18n.fix(pattern), argFn, textFn ? _.compose(textFn, _.noI18n) : _.noI18n)
}

_.noI18n.text = function () {
  return _(arguments).reduce(function (memo, str) {
    return memo.add($.txt(_.noI18n(str)))
  }, $())
}

// extend underscore utilities
_.mixin({
  inspect,
  call,
  now,
  then () {
    return 2116800000000
  },
  copy,
  deepClone,
  lfo,
  printf,
  aprintf,
  filesize,
  ellipsis,
  // makes sure you have an array
  getArray,
  wait,
  cid,
  ecid,
  toHash,
  shorten,
  noI18n,
  ...underscoreExtends
})

export function escapeRegExp (s) {
  return (s || '').replace(/([$^*+?!:=.|(){}[\]\\])/g, function () { return (`\\${arguments[1]}`) })
}

_.escapeRegExp = escapeRegExp

_.extend(_, us)

export default _
